import {
  atom,
  atomFamily,
  DefaultValue,
  RecoilLoadable,
  selector,
} from "recoil";

import { Auth, parseAuthResult } from "@lib/authentication";
import { getProduct, listProducts } from "@pages/ProductCatalog/productApi";
import { listEntitlements } from "@pages/v2/queries/entitlements/entitlementApi";

export interface Entitlement {
  id?: number;
  name: string;
}

// One pricing row per country (native omg-api shape). `active` gates checkout;
// `promotionSlug` is carried through but not edited in the UI.
export interface ProductPricing {
  id: number;
  amount: number;
  strikeThroughAmount: number;
  currency: string;
  countryCode: string;
  startDate: string | null;
  endDate: string | null;
  promotionSlug: string | null;
  active: boolean;
}

export interface Product {
  id: number;
  name: string;
  slug: string | null;
  displayName: string | null;
  active: boolean;
  entitlements: string[];
  pricings: ProductPricing[];
}

// Sentinel id for a not-yet-saved product. Real ids are positive; 0 marks the
// optimistic row so productDetails serves a blank instead of hitting the API.
export const NEW_PRODUCT_ID = 0;

export const blankNewProduct = (): Product => ({
  id: NEW_PRODUCT_ID,
  name: "",
  slug: null,
  displayName: null,
  active: true,
  entitlements: [],
  pricings: [],
});

export enum ProductCatalogField {
  Entitlements,
  Summaries,
}

interface TProductCatalogState {
  entitlements: Entitlement[];
  summaries: Product[];
  products: Record<number, Product>;
  reset?: ProductCatalogField;
  productId?: number;
  currentProduct?: Product;
}

export interface Editable {
  id: string;
  originalId: string;
  deleted?: boolean;
}

// Grid-editable pricing row. The grid keys on a string id (server ids are
// numbers, and update is replace-all so they don't round-trip); `promotionSlug`
// is preserved but not exposed as a column.
export interface EditablePricing extends Editable {
  amount: number;
  strikeThroughAmount: number;
  currency: string;
  countryCode: string;
  startDate?: string | null;
  endDate?: string | null;
  promotionSlug: string | null;
  active: boolean;
}

const productCatalogState = atom<TProductCatalogState>({
  key: "productCatalog",
  default: RecoilLoadable.of(
    (async () => {
      const emptyState = {
        entitlements: [],
        summaries: [],
        products: {},
        productId: undefined,
      };
      try {
        const { accessToken } = parseAuthResult(
          await Auth.currentAuthenticatedUser(),
        );
        if (accessToken) {
          return {
            entitlements: await fetchEntitlements(),
            summaries: await fetchProductSummaries(),
            products: {},
            productId: undefined,
          };
        }

        return emptyState;
      } catch {
        return emptyState;
      }
    })(),
  ),
  effects: [
    ({ setSelf, onSet }) => {
      onSet(async (updatedValue) => {
        switch (updatedValue.reset) {
          case ProductCatalogField.Summaries:
            fetchProductSummaries(setSelf);
            break;

          case ProductCatalogField.Entitlements:
            fetchEntitlements(setSelf);
            break;
        }
        // we want to clear the reset field
        return setSelf({ ...updatedValue, reset: undefined });
      });
    },
  ],
});

async function fetchProductSummaries(
  setState?: (_: (old: any) => TProductCatalogState) => void,
): Promise<Product[]> {
  try {
    const summaries = await listProducts();
    setState && setState((old) => ({ ...old, summaries }));
    return summaries;
  } catch {
    // console.log('Error fetching product summaries');
    return [];
  }
}

async function fetchEntitlements(
  setState?: (_: (old: any) => TProductCatalogState) => void,
): Promise<Entitlement[]> {
  try {
    const entitlements = await listEntitlements();
    setState && setState((old) => ({ ...old, entitlements }));
    return entitlements;
  } catch {
    // console.log('Error fetching entitlements');
    return [];
  }
}

export const productSummaries = selector<Product[]>({
  key: "productSummaries",
  get: ({ get }) => {
    const state = get(productCatalogState);
    return state.summaries;
  },
  set: ({ set }, summaries) => {
    if (summaries instanceof DefaultValue) {
      return set(productCatalogState, (old) => ({
        ...old,
        reset: ProductCatalogField.Summaries,
      }));
    }
    set(productCatalogState, (old) => ({
      ...old,
      summaries,
    }));
  },
});

export const entitlementsState = selector<Entitlement[]>({
  key: "entitlements",
  get: ({ get }) => {
    const state = get(productCatalogState);
    return state.entitlements;
  },
  set: ({ set }, entitlements) => {
    if (entitlements instanceof DefaultValue) {
      return set(productCatalogState, (old) => ({
        ...old,
        reset: ProductCatalogField.Entitlements,
      }));
    }
    set(productCatalogState, (old) => ({
      ...old,
      entitlements,
    }));
  },
});

export const productDetails = atomFamily<Product | null, number | undefined>({
  key: "productDetails",
  default: async (productId?: number) => {
    if (productId === undefined) {
      return null;
    }
    if (productId === NEW_PRODUCT_ID) {
      return blankNewProduct();
    }
    return await getProduct(productId);
  },
});

export const selectedProductState = selector<number | undefined>({
  key: "selectedProduct",
  get: ({ get }) => {
    return get(productCatalogState).productId;
  },
  set: ({ set }, value) => {
    if (value instanceof DefaultValue) {
      value = undefined;
    }

    set(productCatalogState, (old) => ({
      ...old,
      productId: value as number | undefined,
    }));
  },
});

// Name for a product being created (the checkout SKU). Held separately because
// the optimistic summary row carries the sentinel id, not the name to POST.
export const newProductNameState = atom<string>({
  key: "newProductName",
  default: "",
});

export const editProductPricingsState = atom<EditablePricing[]>({
  key: "editProductPricings",
  default: [],
});

export const editEntitlementsState = atom<string[]>({
  key: "editProductEntitlements",
  default: [],
});

export const resetEdits = atom<number>({
  key: "resetEdits",
  default: 0,
});
